import { Box, Button, useTheme } from '@mui/material';
import { ResponsiveStyleValue } from '@mui/system';
import { useContext, useEffect, useMemo, useCallback, type JSX } from 'react';
import { GiftcardPaymentInfo, PaymentInfo } from '@repo/types';
import { useLocale } from '@repo/i18n';
import { MountPointContext } from '@repo/common-utils/mountPoint';
import {
    stripePaymentAtom,
    useStripePayment as useStripePayment,
} from 'src/state/payment/stripePayment.atom';
import { isGiftcardPaymentInfo } from '@repo/widget-utils/payment-types-helper';
import { initStripePayment } from 'src/widgets/stripe/initStripeWidget';

interface IProps {
    paymentInfo: PaymentInfo | GiftcardPaymentInfo;
    onPaymentCancelled: () => void;
    onPaymentCompleted: (
        paymentInfo: PaymentInfo | GiftcardPaymentInfo,
        paymentGatewayResponse: any,
    ) => void;
}

const TEN_MINUTES = 1000 * 60 * 14.5;

export default function StripePaymentWrapper(props: IProps): JSX.Element {
    const { t } = useLocale();
    const timeOfMount = useMemo(() => Date.now(), []);
    const theme = useTheme();
    const mountPoint = useContext(MountPointContext);
    const { paymentStatus, stripeClientSecret } = useStripePayment();

    // The Stripe form lives in a light-DOM child of the host custom element (Stripe Elements
    // refuses shadow DOM) projected through the <slot> below — and slots only project their own
    // host's children. Bundle init only wires the bilberry-checkout singleton, so any other host
    // (e.g. the member-card purchase widget) must get its form on demand.
    useEffect(() => {
        const host = mountPoint.shadowRoot?.host as HTMLElement | undefined;
        if (host) initStripePayment(host);
    }, [mountPoint.shadowRoot]);

    const goBackClicked = useCallback(() => {
        props.onPaymentCancelled();
    }, [props]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (Date.now() - timeOfMount > TEN_MINUTES) {
                alert(t.your_reservation_timed_out);
                goBackClicked();
            }
        }, 5000);
        return () => clearInterval(interval);
    }, [timeOfMount, goBackClicked, t.your_reservation_timed_out]);

    useEffect(() => {
        // The status atom is shared by every mounted wrapper (e.g. checkout + member-card widget),
        // so only complete when the succeeded payment is OURS — the success write preserves the
        // atom's stripeClientSecret, so it identifies which payment the status belongs to.
        const isOwnPayment = stripeClientSecret === getPaymentInfo(props.paymentInfo).clientSecret;
        switch (paymentStatus) {
            case 'succeeded': {
                if (isOwnPayment) props.onPaymentCompleted(props.paymentInfo, paymentStatus);
                break;
            }

            default:
                break;
        }
    }, [paymentStatus, stripeClientSecret, props]);

    useEffect(() => {
        const fn = async () => {
            const { stripeKey, clientSecret } = getPaymentInfo(props.paymentInfo);

            if (!stripeKey || !clientSecret) return;

            await stripePaymentAtom.update({
                ...stripePaymentAtom.subject.value,
                stripeKey,
                stripeClientSecret: clientSecret,
            });
        };
        fn();

        return () => {
            const fn = async () => await stripePaymentAtom.update({ paymentStatus: '' });
            fn();
        };
    }, [props.paymentInfo]);

    return (
        <Box
            color={theme.palette.text.primary}
            fontFamily={theme.typography.fontFamily as ResponsiveStyleValue<any>}
            fontSize={theme.typography.fontSize}
            pt={0}
            minWidth="300px"
            width="100%"
            maxWidth="50%"
            sx={{
                '& > *': {
                    marginTop: theme.spacing(1),
                    marginBottom: theme.spacing(1),
                },

                [theme.breakpoints.down('md')]: {
                    marginBottom: theme.spacing(6),
                    maxWidth: '100%',
                },
            }}
        >
            <Box>
                <slot name="stripe-payment-slot" />
            </Box>
            <Button
                variant="outlined"
                color="primary"
                onClick={goBackClicked}
                sx={{ mt: theme.spacing(-8.5) }}
            >
                {t.go_back}
            </Button>
        </Box>
    );
}

function getPaymentInfo(context: PaymentInfo | GiftcardPaymentInfo) {
    if (isGiftcardPaymentInfo(context))
        return {
            stripeKey: context.giftcard.stripe_key,
            clientSecret: context.giftcard.stripe_client_secret,
        };
    else
        return {
            stripeKey: context.booking?.stripe_key,
            clientSecret: context.booking?.stripe_client_secret,
        };
}
